import React from "react";
import Nav from "react-bootstrap/Nav";
import Cookies from "js-cookie";
import NavDropdown from "react-bootstrap/NavDropdown";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./NavBar.css";
import { useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { logout } from "../../redux/actions/authAction";
import { Dropdown } from "react-bootstrap";
import { getUserCookie } from "../../utils/cookiesUtils";
import {
  openFiftyOne,
  releaseFiftyOneSession,
} from "../../utils/fiftyone";
import daatLogo from "../../assets/DAAT_Logo.png";
import fiftyoneLogo from "../../assets/fiftyone_logo.webp";
import CVATLogo from "../../assets/cvat_logo.png";
import labelStudioLogo from "../../assets/labelstudio_logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBroom,
  faChartLine,
  faDatabase,
  faDownload,
  faFileExport,
  faPenToSquare,
  faRobot,
  faTableCellsLarge,
  faUpload,
  faUserGroup,
  faRightFromBracket,
  faBars,
  faXmark,
  faGear,
} from "@fortawesome/free-solid-svg-icons";

// Navigation items configuration with icons
const navItemsConfig = [
  { key: 'acquisition', path: '/acquisition', labelKey: 'nav.dataAcquisition', icon: faUpload, roles: ['admin', 'developer', 'annotator'] },
  { key: 'data-processing', path: '/data-processing', labelKey: 'nav.dataProcessing', icon: faBroom, roles: ['admin', 'developer', 'annotator'] },
  { key: 'pre-label', path: '/pre-label', labelKey: 'nav.preLabel', icon: faRobot, roles: ['admin', 'developer', 'annotator'] },
  { key: 'labels', path: '/labels', labelKey: 'nav.labelOperations', icon: faPenToSquare, roles: ['admin', 'developer', 'annotator'] },
  { key: 'dataset', path: '/dataset', labelKey: 'nav.datasetSplit', icon: faDatabase, roles: ['admin', 'developer'] },
  { key: 'export', path: '/export', labelKey: 'nav.export', icon: faFileExport, roles: ['admin', 'developer'] },
  { key: 'ETL', path: '/ETL', labelKey: 'nav.etl', icon: faTableCellsLarge, roles: ['admin', 'developer', 'annotator'] },
  { key: 'train', path: '/train', labelKey: 'nav.train', icon: faChartLine, roles: ['admin', 'developer', 'annotator'] },
  { key: 'nef', path: '/nef', labelKey: 'nav.nef', icon: faDownload, roles: ['admin', 'developer', 'annotator'] },
];

const annotationItems = [
  { eventKey: "cvat", href: "/cvat", label: "CVAT" },
  { eventKey: "label-studio", href: "/label-studio", label: "Label Studio" },
];

// Helper function to get user initials
const getInitials = (email) => {
  if (!email) return 'U';
  return email.charAt(0).toUpperCase();
};

function NavigationBar(props) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedAnnotation, setSelectedAnnotation] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const userData = getUserCookie();
  const role = userData ? userData.role : null;
  const organizationRole = userData ? userData.organization_role : null;
  const displayRole = organizationRole || role;
  const organization = userData ? userData.organization : null;

  // 使用 useMemo 缓存权限检查结果，避免重复计算
  const allowedPaths = useMemo(() => ({
    admin: ["/*"], // Allows access to all paths
    developer: ["/*"], // Allows access to all paths
    annotator: ["/*"],
  }), []);

  const isAllowedToClick = useCallback((path) => {
    const allowed = allowedPaths[role] || [];
    if (allowed.includes(path) || allowed.includes("/*")) {
      return true;
    }
    return false;
  }, [role, allowedPaths]);

  const integratedToolUrls = useMemo(() => {
    const cvat =
      process.env.REACT_APP_INTEGRATED_CVAT_URL ||
      "https://cvat.kneron.com/";
    const labelStudio =
      process.env.REACT_APP_INTEGRATED_LABEL_STUDIO_URL ||
      "http://daat.kneron.com:8081/";
    return { cvat, labelStudio };
  }, []);

  const displayAnnotationItems = annotationItems;
  const selectedAnnotationItem = displayAnnotationItems.find(
    (item) => item.eventKey === selectedAnnotation
  );
  const annotationTitle = selectedAnnotationItem?.label || t("nav.annotation");

  useEffect(() => {
    const selectedItem = annotationItems.find((item) =>
      location.pathname.includes(item.eventKey)
    );
    setSelectedAnnotation(selectedItem?.eventKey || "");
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleDropdownSelect = (eventKey, dropdownItems) => {
    const selectedItem = dropdownItems.find(
      (item) => item.eventKey === eventKey
    );
    if (selectedItem) {
      setSelectedAnnotation(selectedItem.eventKey);
    }
  };

  const onLogOut = async () => {
    await releaseFiftyOneSession();
    localStorage.removeItem("token");
    props.logout();
    window.history.replaceState(null, "", "/signin");
    Cookies.remove("user_token");
    navigate("/signin");
  };

  const logined = (
    <div className="user-section">
      {/* User Dropdown */}
      <Dropdown className="user-dropdown">
        <Dropdown.Toggle variant="light" id="user-dropdown" className="user-toggle">
          <div className="user-avatar">
            {getInitials(props.user.user.email)}
          </div>
          <div className="user-info">
            <div className="user-email">{props.user.user.email ? props.user.user.email.split('@')[0] : ''}</div>
            {organization && (
              <div className="user-org">{organization}</div>
            )}
          </div>
          <span className="dropdown-arrow">▼</span>
        </Dropdown.Toggle>
        
        <Dropdown.Menu className="user-menu">
          <div className="menu-header">
            <div className="user-avatar-large">
              {getInitials(props.user.user.email)}
            </div>
            <div className="user-details">
              <div className="user-name">{props.user.user.email}</div>
              <div className="user-role">{displayRole}</div>
            </div>
          </div>
          
          <Dropdown.Divider />
          
          {role === "admin" && (
            <>
              <Dropdown.Item
                href={`http://${window.location.hostname}:5001/admin`}
                target="_blank"
                className="menu-item"
              >
                <FontAwesomeIcon className="item-icon" icon={faChartLine} />
                <span>{t("nav.dashboard")}</span>
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => navigate("/organization")}
                className="menu-item"
              >
                <FontAwesomeIcon className="item-icon" icon={faUserGroup} />
                <span>{t("nav.organizations")}</span>
              </Dropdown.Item>
              <Dropdown.Divider />
            </>
          )}
          
          {organization && (
            <>
              <Dropdown.Item
                onClick={() => navigate("/organization")}
                className="menu-item"
              >
                <FontAwesomeIcon className="item-icon" icon={faUserGroup} />
                <span>{t("nav.organization", { organization })}</span>
              </Dropdown.Item>
              <Dropdown.Divider />
            </>
          )}

          <Dropdown.Item
            onClick={() => navigate("/setting")}
            className="menu-item"
          >
            <FontAwesomeIcon className="item-icon" icon={faGear} />
            <span>{t("nav.systemSettings")}</span>
          </Dropdown.Item>
          
          <Dropdown.Item onClick={onLogOut} className="menu-item logout">
            <FontAwesomeIcon className="item-icon" icon={faRightFromBracket} />
            <span>{t("nav.logOut")}</span>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  const login = (
    <Link to="/signin" onClick={() => navigate("/signin")}>
      {t("nav.signIn")}
    </Link>
  );

  return (
    <div className="navbar-container">
      <div
        className={`navbar navbar-expand-lg ${
          mobileMenuOpen ? "mobile-menu-open" : ""
        }`}
      >
        {/* Brand Section */}
        <div className="brand-section">
          <div className="main-brand">
            <img src={daatLogo} className="daat-logo" alt="DAAT Logo" />
          </div>
          
          <div className="integrated-tools">
            <button
              type="button"
              onClick={() => openFiftyOne()}
              className="tool-logo-link"
              title="FiftyOne"
              aria-label={t("nav.openTool", { tool: "FiftyOne" })}
            >
              <img src={fiftyoneLogo} alt="FiftyOne" />
            </button>
            <button
              type="button"
              onClick={() => {
                const w = window.open("about:blank");
                w.location.href = integratedToolUrls.cvat;
              }}
              className="tool-logo-link"
              title="CVAT"
              aria-label={t("nav.openTool", { tool: "CVAT" })}
            >
              <img src={CVATLogo} alt="CVAT" />
            </button>
            <button
              type="button"
              onClick={() => {
                const w = window.open("about:blank");
                w.location.href = integratedToolUrls.labelStudio;
              }}
              className="tool-logo-link"
              title="Label Studio"
              aria-label={t("nav.openTool", { tool: "Label Studio" })}
            >
              <img src={labelStudioLogo} alt="Label Studio" />
            </button>
          </div>
        </div>

        <button
          type="button"
          className="navbar-mobile-toggle"
          aria-label={t(
            mobileMenuOpen ? "nav.closeNavigation" : "nav.openNavigation"
          )}
          aria-expanded={mobileMenuOpen}
          aria-controls="primary-navigation"
          title={t(
            mobileMenuOpen ? "nav.closeNavigation" : "nav.openNavigation"
          )}
          onClick={() => setMobileMenuOpen((open) => !open)}
        >
          <FontAwesomeIcon icon={mobileMenuOpen ? faXmark : faBars} />
        </button>

        {/* Navigation Section */}
        <div className="container-fluid" id="primary-navigation">
          <Nav variant="pills" defaultActiveKey="/acquisition">
            <div className="right-navbar-container">
              <div className="nav-items-center">
                {navItemsConfig.map((item) => {
                  // Check if user role has access to this nav item
                  if (!item.roles.includes(role)) return null;

                  // Insert Annotation dropdown before ETL
                  if (item.key === 'ETL') {
                    return (
                      <React.Fragment key="annotation-and-etl">
                        <CustomizedNavDropdown
                          title={annotationTitle}
                          selectedItem={selectedAnnotation}
                          dropdownItems={displayAnnotationItems}
                          handleDropdownSelect={handleDropdownSelect}
                          isAllowedToClick={isAllowedToClick}
                          location={location}
                        />
                        <div className="nav-item-container" key={item.key}>
                          <Nav.Link
                            eventKey={item.key}
                            as={Link}
                            to={item.path}
                            active={location.pathname === item.path}
                            disabled={!isAllowedToClick(item.path)}
                            className="nav-link-enhanced"
                          >
                            <FontAwesomeIcon className="nav-icon" icon={item.icon} />
                            <span className="nav-label">{t(item.labelKey)}</span>
                            <span className="nav-indicator"></span>
                          </Nav.Link>
                        </div>
                      </React.Fragment>
                    );
                  }

                  return (
                    <div className="nav-item-container" key={item.key}>
                      <Nav.Link
                        eventKey={item.key}
                        as={Link}
                        to={item.path}
                        active={location.pathname === item.path}
                        disabled={!isAllowedToClick(item.path)}
                        className="nav-link-enhanced"
                      >
                        <FontAwesomeIcon className="nav-icon" icon={item.icon} />
                        <span className="nav-label">{t(item.labelKey)}</span>
                        <span className="nav-indicator"></span>
                      </Nav.Link>
                    </div>
                  );
                })}
              </div>

              <div className="navbar-user-slot">
                {props.user.user.email ? logined : login}
              </div>
            </div>
          </Nav>
        </div>
      </div>
    </div>
  );
}

function CustomizedNavDropdown(props) {
  const {
    title,
    selectedItem,
    dropdownItems,
    handleDropdownSelect,
    isAllowedToClick,
    location,
  } = props;

  return (
    <div className="nav-item-container">
      <NavDropdown
        title={title}
        id="nav-dropdown"
        active={selectedItem !== ""}
        onSelect={(eventKey) =>
          handleDropdownSelect(eventKey, dropdownItems)
        }
        className="nav-dropdown"
      >
        {dropdownItems.map((item) => (
          <NavDropdown.Item
            key={item.eventKey}
            eventKey={item.eventKey}
            as={Link}
            to={item.href}
            active={location.pathname === item.href}
            disabled={!isAllowedToClick(item.href)}
          >
            {item.label}
          </NavDropdown.Item>
        ))}
      </NavDropdown>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    user: state.auth,
  };
};

export default connect(mapStateToProps, { logout })(NavigationBar);
